import { Box, Button, Container, Typography } from "@mui/material";
import OptimizedImage from "../ui/OptimizedImage";
import { assortmentHighlights, assortmentImages } from "../../data/assortment";

const colors = {
    cream: "#fffaf2",
    sand: "#f7f1e8",
    paper: "rgba(255, 250, 242, 0.88)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    blue: "#2f91b8",
};

const teaTypes = [
    "Schwarzer Tee",
    "Grüner Tee",
    "Oolong",
    "Rooibos",
    "Früchtetee",
    "Kräutertee",
    "Eistee",
    "Teezubehör",
];

function ImageRow({ images, rounded = true, gapMobile = 1.5 }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    md: `repeat(${images.length}, 1fr)`,
                },
                gap: {
                    xs: gapMobile,
                    md: 0,
                },
                width: "100%",
                overflow: "hidden",
                backgroundColor: colors.cream,
                borderRadius: rounded
                    ? {
                        xs: 4,
                        md: 0,
                    }
                    : 0,
            }}
        >
            {images.map((image) => (
                <Box
                    key={image.imageKey}
                    sx={{
                        width: "100%",
                        backgroundColor: colors.cream,
                        borderRight: {
                            xs: "none",
                            md: "2px solid #fff",
                        },

                        "&:last-of-type": {
                            borderRight: "none",
                        },
                    }}
                >
                    <OptimizedImage
                        imageKey={image.imageKey}
                        alt={image.alt}
                        sizes="(max-width: 900px) 100vw, 33vw"
                        sx={{
                            width: "100%",
                            height: "auto",
                            backgroundColor: colors.cream,
                        }}
                        imgSx={{
                            width: "100%",
                            height: "auto",
                        }}
                        objectFit="contain"
                        objectPosition="center"
                    />
                </Box>
            ))}
        </Box>
    );
}

function AssortmentSection() {
    return (
        <Box
            component="section"
            id="sortiment"
            sx={{
                position: "relative",
                overflow: "hidden",
                py: {
                    xs: 8,
                    md: 13,
                },
                background:
                    "linear-gradient(180deg, #f2eadc 0%, #fffaf2 36%, #eef7fa 100%)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 120,
                    left: {
                        xs: -140,
                        md: -180,
                    },
                    width: {
                        xs: 300,
                        md: 460,
                    },
                    height: {
                        xs: 300,
                        md: 460,
                    },
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(47,145,184,0.12) 0%, rgba(47,145,184,0) 68%)",
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    right: {
                        xs: -160,
                        md: -120,
                    },
                    bottom: 420,
                    width: {
                        xs: 280,
                        md: 430,
                    },
                    height: {
                        xs: 280,
                        md: 430,
                    },
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(155,118,83,0.15) 0%, rgba(155,118,83,0) 70%)",
                    pointerEvents: "none",
                }}
            />

            <Container
                maxWidth={false}
                sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 1180,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                <Box
                    sx={{
                        textAlign: {
                            xs: "left",
                            md: "center",
                        },
                        maxWidth: 820,
                        mx: "auto",
                        mb: {
                            xs: 5,
                            md: 8,
                        },
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            mb: 1.2,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 13,
                            fontWeight: 900,
                            letterSpacing: "0.17em",
                            textTransform: "uppercase",
                            color: colors.brown,
                        }}
                    >
                        Unser Sortiment
                    </Typography>

                    <Typography
                        component="h2"
                        sx={{
                            mb: 2.5,
                            fontFamily: '"Quicksand", sans-serif',
                            fontSize: {
                                xs: "2.45rem",
                                sm: "3.2rem",
                                md: "4.4rem",
                            },
                            lineHeight: 0.98,
                            fontWeight: 700,
                            letterSpacing: "-0.06em",
                            color: colors.ink,
                        }}
                    >
                        Mode, Tee und Mee(h)r
                    </Typography>

                    <Typography
                        sx={{
                            maxWidth: 720,
                            mx: {
                                md: "auto",
                            },
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: {
                                xs: 17,
                                md: 19,
                            },
                            lineHeight: 1.75,
                            color: colors.muted,
                        }}
                    >
                        In unserem Lädchen finden Sie eine liebevoll abgestimmte Auswahl
                        aus Mode, Outdoorbekleidung, Schuhen, Tee, Accessoires und
                        maritimen Geschenkideen.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        mb: {
                            xs: 4,
                            md: 6,
                        },
                        mx: {
                            xs: -2,
                            sm: -3,
                            md: "calc((1180px - 100vw) / 2)",
                        },
                    }}
                >
                    <ImageRow images={assortmentImages.main} />
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "0.95fr 1.05fr",
                        },
                        gap: {
                            xs: 4,
                            md: 7,
                        },
                        alignItems: "start",
                        mb: {
                            xs: 8,
                            md: 13,
                        },
                    }}
                >
                    <Box>
                        <Typography
                            component="h3"
                            sx={{
                                mb: 2.5,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2rem",
                                    md: "2.9rem",
                                },
                                lineHeight: 1.05,
                                fontWeight: 700,
                                letterSpacing: "-0.045em",
                                color: colors.ink,
                            }}
                        >
                            Erstaunlich, was alles in so ein Lädchen passt.
                        </Typography>

                        <Typography
                            sx={{
                                mb: 3,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16.5,
                                    md: 18,
                                },
                                lineHeight: 1.75,
                                color: colors.muted,
                            }}
                        >
                            Ob sportlich-schicke Mode, der klassische Friesennerz,
                            farbenfrohe Gummistiefel, bequeme Schuhe, passende Handtaschen,
                            Ledergürtel, Lederjacken oder maritime Geschenkartikel — am
                            besten überzeugen Sie sich direkt vor Ort.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                            },
                            gap: 1.2,
                        }}
                    >
                        {assortmentHighlights.map((item) => (
                            <Box
                                key={item}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.2,
                                    p: 1.4,
                                    borderRadius: 3,
                                    backgroundColor: "rgba(255,250,242,0.72)",
                                    border: "1px solid rgba(155,118,83,0.13)",
                                }}
                            >
                                <Box
                                    sx={{
                                        mt: "0.48em",
                                        width: 7,
                                        height: 7,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        backgroundColor: colors.brown,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 15,
                                        lineHeight: 1.45,
                                        fontWeight: 700,
                                        color: "rgba(42,38,33,0.72)",
                                    }}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    id="mode"
                    sx={{
                        mb: {
                            xs: 4,
                            md: 6,
                        },
                        mx: {
                            xs: -2,
                            sm: -3,
                            md: "calc((1180px - 100vw) / 2)",
                        },
                    }}
                >
                    <ImageRow images={assortmentImages.fashion} />
                </Box>

                <Box
                    sx={{
                        maxWidth: 900,
                        mx: "auto",
                        mb: {
                            xs: 8,
                            md: 13,
                        },
                        textAlign: {
                            xs: "left",
                            md: "center",
                        },
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            mb: 1.1,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 13,
                            fontWeight: 900,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.brown,
                        }}
                    >
                        Unsere Mode
                    </Typography>

                    <Typography
                        component="h3"
                        sx={{
                            mb: 2.5,
                            fontFamily: '"Quicksand", sans-serif',
                            fontSize: {
                                xs: "2.2rem",
                                md: "3.25rem",
                            },
                            lineHeight: 1.02,
                            fontWeight: 700,
                            letterSpacing: "-0.055em",
                            color: colors.ink,
                        }}
                    >
                        Monatlich neue Kollektionen — persönlich ausgewählt.
                    </Typography>

                    <Typography
                        sx={{
                            mb: 2.4,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: {
                                xs: 16.5,
                                md: 18,
                            },
                            lineHeight: 1.75,
                            color: colors.muted,
                        }}
                    >
                        Alle vier Wochen geht es ins Modezentrum nach Hamburg. Dort
                        entstehen neue, aufeinander abgestimmte Kollektionen mit vielen
                        Kombinationsmöglichkeiten — vom Loop über Shirt, Bluse, Hose, Rock
                        oder Kleid bis zum passenden Blazer.
                    </Typography>

                    <Typography
                        sx={{
                            mb: 3.2,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: {
                                xs: 16.5,
                                md: 18,
                            },
                            lineHeight: 1.75,
                            color: colors.muted,
                        }}
                    >
                        More & More führen wir in den Größen 34–46, comma in den Größen
                        34–44. Viele Stücke bestehen überwiegend aus angenehmen Naturfasern
                        wie Baumwolle, Viskose, Leinen und Tencel.
                    </Typography>

                    <Box
                        sx={{
                            p: {
                                xs: 2.4,
                                md: 3,
                            },
                            borderRadius: 4,
                            backgroundColor: colors.paper,
                            border: "1px solid rgba(155,118,83,0.15)",
                            boxShadow: "0 18px 52px rgba(78, 54, 30, 0.08)",
                            textAlign: "left",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "1.35rem",
                                    md: "1.65rem",
                                },
                                lineHeight: 1.28,
                                fontWeight: 700,
                                color: colors.brownDark,
                            }}
                        >
                            Individuelle Beratung — offen, ehrlich und mit viel Liebe zum
                            Detail.
                        </Typography>

                        <Typography
                            sx={{
                                mt: 1.2,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 15.8,
                                lineHeight: 1.65,
                                color: colors.muted,
                            }}
                        >
                            Termine außerhalb der Öffnungszeiten sind nach Absprache
                            möglich — auch in der Winterpause.
                        </Typography>
                    </Box>
                </Box>

                <Box
                    id="tee"
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "0.95fr 1.05fr",
                        },
                        gap: {
                            xs: 4,
                            md: 7,
                        },
                        alignItems: "center",
                        mb: {
                            xs: 8,
                            md: 13,
                        },
                    }}
                >
                    <Box
                        sx={{
                            p: {
                                xs: 2.5,
                                md: 4,
                            },
                            borderRadius: {
                                xs: 5,
                                md: 6,
                            },
                            background:
                                "linear-gradient(135deg, rgba(255,250,242,0.94), rgba(247,241,232,0.84))",
                            border: "1px solid rgba(155,118,83,0.15)",
                            boxShadow: "0 22px 70px rgba(78, 54, 30, 0.09)",
                        }}
                    >
                        <Typography
                            component="p"
                            sx={{
                                mb: 1.1,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.brown,
                            }}
                        >
                            Auch online bestellbar
                        </Typography>

                        <Typography
                            component="h3"
                            sx={{
                                mb: 2.5,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.2rem",
                                    md: "3.25rem",
                                },
                                lineHeight: 1.02,
                                fontWeight: 700,
                                letterSpacing: "-0.055em",
                                color: colors.ink,
                            }}
                        >
                            Unser Tee Sortiment
                        </Typography>

                        <Typography
                            sx={{
                                mb: 2.4,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16.5,
                                    md: 18,
                                },
                                lineHeight: 1.75,
                                color: colors.muted,
                            }}
                        >
                            Unser stattliches Teesortiment mit inzwischen 79 verschiedenen
                            Sorten kann sich sehen lassen. Wir führen kräftigen Schwarzen
                            Tee, wie ihn der Ostfriese liebt, aromatische Mischungen,
                            Oolong, Grünen Tee, Rooibos, Früchtetee und wärmende
                            Kräutertees.
                        </Typography>

                        <Typography
                            sx={{
                                mb: 2.4,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16.5,
                                    md: 18,
                                },
                                lineHeight: 1.75,
                                color: colors.muted,
                            }}
                        >
                            Für Kinder ist Rooibos besonders beliebt, da er komplett ohne
                            Koffein auskommt. Bei unseren jüngsten Kunden punktet außerdem
                            immer wieder unser „Gummibärchen-Tee“. Und wenn einmal
                            Schietwetter aufzieht, wärmt eine schöne Tasse Kräutertee
                            wieder auf.
                        </Typography>

                        <Typography
                            sx={{
                                mb: 3,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16.5,
                                    md: 18,
                                },
                                lineHeight: 1.75,
                                color: colors.muted,
                            }}
                        >
                            An heißen Tagen sorgen unsere fruchtigen Eistees für
                            Erfrischung: abends gekocht, über Nacht in den Kühlschrank
                            gestellt und am nächsten Tag eiskalt genießen.
                        </Typography>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                },
                                gap: 1.2,
                                mb: 3,
                            }}
                        >
                            {teaTypes.map((item) => (
                                <Box
                                    key={item}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.4,
                                        py: 1.1,
                                        borderRadius: 999,
                                        backgroundColor: "rgba(155,118,83,0.09)",
                                        color: colors.brownDark,
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 14.5,
                                        fontWeight: 900,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: "50%",
                                            backgroundColor: colors.brown,
                                            flexShrink: 0,
                                        }}
                                    />
                                    {item}
                                </Box>
                            ))}
                        </Box>

                        <Button
                            href="https://www.hooksieler-laedchen.de"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            sx={{
                                minHeight: 52,
                                px: 3.5,
                                borderRadius: 999,
                                backgroundColor: colors.brownDark,
                                color: "#fff",
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 14,
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                boxShadow: "none",

                                "&:hover": {
                                    backgroundColor: "#5e422a",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            Tee online bestellen
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: {
                                xs: 5,
                                md: 6,
                            },
                            boxShadow: "0 26px 80px rgba(78,54,30,0.16)",
                            backgroundColor: colors.sand,
                        }}
                    >
                        <OptimizedImage
                            imageKey={assortmentImages.main[2].imageKey}
                            alt={assortmentImages.main[2].alt}
                            sizes="(max-width: 900px) 100vw, 50vw"
                            sx={{
                                width: "100%",
                                height: {
                                    xs: "auto",
                                    md: 640,
                                },
                            }}
                            imgSx={{
                                width: "100%",
                                height: {
                                    xs: "auto",
                                    md: "100%",
                                },
                            }}
                            objectFit={{
                                xs: "contain",
                                md: "cover",
                            }}
                            objectPosition="center"
                        />

                        <Box
                            sx={{
                                position: {
                                    xs: "relative",
                                    md: "absolute",
                                },
                                left: {
                                    md: 22,
                                },
                                right: {
                                    md: 22,
                                },
                                bottom: {
                                    md: 22,
                                },
                                p: {
                                    xs: 2.4,
                                    md: 2.4,
                                },
                                borderRadius: {
                                    xs: 0,
                                    md: 4,
                                },
                                backgroundColor: "rgba(255,250,242,0.92)",
                                backdropFilter: {
                                    md: "blur(8px)",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontSize: {
                                        xs: "1.35rem",
                                        md: "1.65rem",
                                    },
                                    lineHeight: 1.25,
                                    fontWeight: 700,
                                    color: colors.ink,
                                }}
                            >
                                Tee trinken bedeutet genießen und entspannen.
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 1,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 15.5,
                                    lineHeight: 1.6,
                                    color: colors.muted,
                                }}
                            >
                                Tee ist ein Naturprodukt — der Kauf ist Vertrauenssache.
                                Deshalb führen wir ausgesuchte Teesorten mit hohem
                                Qualitätsniveau.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: {
                            xs: 5,
                            md: 7,
                        },
                        background:
                            "linear-gradient(135deg, #806044 0%, #5e422a 58%, #2f91b8 145%)",
                        color: "#fff",
                        boxShadow: "0 28px 90px rgba(78,54,30,0.25)",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: -90,
                            right: -80,
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.08)",
                            pointerEvents: "none",
                        }}
                    />

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            p: {
                                xs: 3,
                                sm: 4,
                                md: 5,
                            },
                            textAlign: {
                                xs: "left",
                                md: "center",
                            },
                        }}
                    >
                        <Typography
                            component="p"
                            sx={{
                                mb: 1,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.72)",
                            }}
                        >
                            Gürtel & Wechselschließen
                        </Typography>

                        <Typography
                            component="h3"
                            sx={{
                                maxWidth: 760,
                                mx: {
                                    md: "auto",
                                },
                                mb: 2.2,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.05rem",
                                    sm: "2.65rem",
                                    md: "3.6rem",
                                },
                                lineHeight: 1,
                                fontWeight: 700,
                                letterSpacing: "-0.055em",
                            }}
                        >
                            Stell dir deinen Gürtel selbst zusammen.
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 700,
                                mx: {
                                    md: "auto",
                                },
                                mb: 3.5,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16.5,
                                    md: 18,
                                },
                                lineHeight: 1.7,
                                color: "rgba(255,255,255,0.86)",
                            }}
                        >
                            Wählen Sie aus hochwertigen Ledergürteln in trendigen Farben
                            und kombinieren Sie diese mit einer ausgefallenen
                            Wechselschließe. Viele Varianten können direkt bestellt werden.
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: {
                                    xs: "column",
                                    sm: "row",
                                },
                                justifyContent: {
                                    md: "center",
                                },
                                gap: 1.5,
                                mb: {
                                    xs: 3,
                                    md: 4,
                                },
                            }}
                        >
                            <Button
                                href="https://www.hooksieler-laedchen.de"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                sx={{
                                    minHeight: 52,
                                    px: 3.5,
                                    borderRadius: 999,
                                    backgroundColor: colors.cream,
                                    color: colors.brownDark,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 14,
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    boxShadow: "none",

                                    "&:hover": {
                                        backgroundColor: "#fff",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                Zum Bestell-Shop
                            </Button>
                        </Box>

                        <Box
                            sx={{
                                mx: {
                                    xs: -1.8,
                                    sm: -2.5,
                                    md: -3.5,
                                },
                                mb: {
                                    xs: -1.8,
                                    sm: -2.5,
                                    md: -3.5,
                                },
                            }}
                        >
                            <ImageRow
                                images={assortmentImages.belts}
                                rounded={false}
                                gapMobile={1.2}
                            />
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

export default AssortmentSection;